<script setup>
/**
 * What the ticket is about: its title, its description, the steps it needs and
 * the conversations it came from.
 *
 * Both texts are edited in place, and both commit the same way — trim, then send
 * only if the words actually moved. The two differ in one deliberate way: a
 * title is a line, so Enter commits it; a description is a paragraph, so Enter
 * belongs to the text and the accelerator commits instead. An emptied title is
 * not an edit (a ticket without one is not a ticket); an emptied description is,
 * because the column is nullable and clearing it is a thing an agent means.
 *
 * Nothing is decided here. The pane holds the two drafts and emits everything
 * else, so the screen that owns the store stays the only place a write is fired.
 */
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import CaseLinkedConversations from './CaseLinkedConversations.vue';
import CaseTasksPanel from './CaseTasksPanel.vue';

const props = defineProps({
  supportCase: {
    type: Object,
    required: true,
  },
  tasks: {
    type: Array,
    default: () => [],
  },
  // The screen's read-only verdict: busy or archived.
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'updateTitle',
  'updateDescription',
  'addTask',
  'toggleTask',
  'archiveTask',
  'openConversation',
  'unlink',
]);

const { t } = useI18n();

const isEditingTitle = ref(false);
const draftTitle = ref('');

const isEditingDescription = ref(false);
const draftDescription = ref('');

const description = computed(() => props.supportCase.description || '');

const links = computed(() => props.supportCase.conversationLinks || []);

const startTitleEdit = () => {
  if (props.disabled) return;
  draftTitle.value = props.supportCase.title || '';
  isEditingTitle.value = true;
};

const cancelTitleEdit = () => {
  isEditingTitle.value = false;
};

// An unchanged or emptied title would answer 200 and emit nothing, so the
// request is not worth making. Both sides are trimmed before they are compared,
// so whitespace the editor would strip anyway is not mistaken for an edit.
const commitTitle = () => {
  if (props.disabled) return;
  if (!isEditingTitle.value) return;
  isEditingTitle.value = false;
  const title = draftTitle.value.trim();
  if (!title || title === (props.supportCase.title || '').trim()) return;
  emit('updateTitle', title);
};

const startDescriptionEdit = () => {
  if (props.disabled) return;
  draftDescription.value = description.value;
  isEditingDescription.value = true;
};

const cancelDescriptionEdit = () => {
  isEditingDescription.value = false;
};

// Only "unchanged" is a no-op here: an emptied description is a clear, which the
// command accepts and the column holds.
const commitDescription = () => {
  if (props.disabled) return;
  if (!isEditingDescription.value) return;
  isEditingDescription.value = false;
  const next = draftDescription.value.trim();
  if (next === description.value.trim()) return;
  emit('updateDescription', next);
};

// A ticket replaced under the pane leaves no half-finished edit behind, and
// certainly not one that would be saved onto the ticket that replaced it.
watch(
  () => props.supportCase.id,
  () => {
    isEditingTitle.value = false;
    isEditingDescription.value = false;
  }
);
</script>

<template>
  <div class="flex flex-col min-w-0 gap-6" data-testid="case-content-pane">
    <div class="min-w-0">
      <Input
        v-if="isEditingTitle"
        v-model="draftTitle"
        autofocus
        :disabled="disabled"
        :aria-label="t('SUPPORT_CASES.DETAIL.TITLE_LABEL')"
        data-testid="case-title-input"
        @enter="commitTitle"
        @blur="commitTitle"
        @keydown.esc="cancelTitleEdit"
      />
      <button
        v-else
        type="button"
        class="w-full min-w-0 px-2 py-1 -mx-2 text-lg font-medium text-left rounded-lg text-n-slate-12 enabled:hover:bg-n-alpha-1"
        :disabled="disabled"
        :aria-label="t('SUPPORT_CASES.DETAIL.EDIT_TITLE')"
        data-testid="case-title"
        @click="startTitleEdit"
      >
        {{ supportCase.title }}
      </button>
    </div>

    <section class="flex flex-col gap-2 min-w-0">
      <h3 class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t('SUPPORT_CASES.DETAIL.DESCRIPTION.LABEL') }}
      </h3>

      <TextArea
        v-if="isEditingDescription"
        v-model="draftDescription"
        auto-height
        autofocus
        :disabled="disabled"
        :placeholder="t('SUPPORT_CASES.DETAIL.DESCRIPTION.PLACEHOLDER')"
        data-testid="case-description-input"
        @blur="commitDescription"
        @keydown.esc="cancelDescriptionEdit"
        @keydown.meta.enter="commitDescription"
        @keydown.ctrl.enter="commitDescription"
      />
      <button
        v-else
        type="button"
        class="w-full min-w-0 px-2 py-1 -mx-2 text-sm text-left rounded-lg whitespace-pre-wrap enabled:hover:bg-n-alpha-1"
        :class="description ? 'text-n-slate-11' : 'text-n-slate-10'"
        :disabled="disabled"
        :aria-label="t('SUPPORT_CASES.DETAIL.DESCRIPTION.EDIT')"
        data-testid="case-description"
        @click="startDescriptionEdit"
      >
        {{ description || t('SUPPORT_CASES.DETAIL.DESCRIPTION.EMPTY') }}
      </button>
    </section>

    <CaseTasksPanel
      :tasks="tasks"
      :disabled="disabled"
      @add="emit('addTask', $event)"
      @toggle="emit('toggleTask', $event)"
      @archive="emit('archiveTask', $event)"
    />

    <CaseLinkedConversations
      :links="links"
      :disabled="disabled"
      @open="emit('openConversation', $event)"
      @unlink="emit('unlink', $event)"
    />
  </div>
</template>
